import { Info } from '@Components/Common/Info';
import TextEllipsis from '@Components/Common/Tooltip/TextEllipsis';
import { storeSelector, usePolicyContext } from '@Policy/store';
import {
  AnomalyActionData,
  raiActionDataSelector,
} from '@Policy/store/filters/riskAI';
import { ActionEnum, MergedRiskAiFilterRule } from '@Policy/utils/types';
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import {
  Stack,
  LinkButton,
  Table,
  TableHeader,
  TableRow,
  TableCell,
  TableBody,
  useColorMode,
  useColorStyle,
  Flex,
  Text,
} from '@tonic-one/react';
import { handleRedirectClick } from '@Utils/common';
import isEqual from 'lodash/isEqual';
import { ReactNode, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CorrelatedRuleAggressivity } from 'src/pages/GlobalSetting/CorrelatedRuleTemplates/types';
import { CorrelatedTemplateUrl } from 'src/pages/GlobalSetting/CorrelatedRuleTemplates/utils';
import { ActionTableHeader } from '../../../Components/Actions';
import { EditableActionCell } from './EditableActionCell';
import { EditableActionModal } from './EditableActionModal';

export const RuleAggressivitiesActions = (props: {
  preDefinedRules: MergedRiskAiFilterRule[];
}) => {
  const { preDefinedRules } = props;
  const { t } = useTranslation();
  const contextStore = usePolicyContext();
  const contextSelector = storeSelector(contextStore);
  const {
    extraAggressiveActionData,
    aggressiveActionData,
    moderateActionData,
  } = contextSelector(raiActionDataSelector, isEqual);
  const [actionDataToEdit, setActionDataToEdit] = useState<
    ({ aggressivityLevel: string } & AnomalyActionData) | null
  >();

  const aggressivityCounts = useMemo(() => {
    const counts = {
      [CorrelatedRuleAggressivity.SuperAggressive]: 0,
      [CorrelatedRuleAggressivity.Aggressive]: 0,
      [CorrelatedRuleAggressivity.Moderate]: 0,
    };
    preDefinedRules.forEach((rule) => {
      counts[rule.tmRaiRule.raiAggressiveLevel] += 1;
    });
    return counts;
  }, [preDefinedRules]);

  const handleActionEdit = useCallback(
    (actionData: AnomalyActionData, aggressivityLevel: string) => {
      setActionDataToEdit({
        ...actionData,
        subjectTag:
          actionData.subjectTag ||
          t('policy.policyTemplate.tmRiskAiFilters.raiRuleSubjectTag'),
        disclaimer:
          actionData.disclaimer ||
          t('policy.policyTemplate.tmRiskAiFilters.raiRuleDisclaimer'),
        aggressivityLevel,
      });
    },
    [t]
  );

  const handleNavigateToGlobalSettings = useCallback(
    (aggressivityLevel: CorrelatedRuleAggressivity) => {
      const url = `${CorrelatedTemplateUrl.rules}?aggressivityLevel=${aggressivityLevel}`;
      handleRedirectClick(url, '_blank');
    },
    []
  );

  const Section = (props: { children: ReactNode }) => (
    <Stack gap="2x">{props.children}</Stack>
  );

  const SectionHeader = (props: {
    label: string;
    info: string;
    aggressivityLevel: CorrelatedRuleAggressivity;
    count: number;
  }) => {
    const { label, info, aggressivityLevel, count } = props;
    return (
      <Stack gap="2x" direction="row" alignItems="center">
        <ActionTableHeader>{label}</ActionTableHeader>
        <Stack alignItems="center" flexDirection="row" gap="1x">
          <Info content={info} placement="top-start" />
          <Text>:</Text>
        </Stack>
        <LinkButton
          fontSize="md"
          onClick={() => handleNavigateToGlobalSettings(aggressivityLevel)}
        >
          ({count})
        </LinkButton>
      </Stack>
    );
  };

  return (
    <Stack gap="4x">
      <Section>
        <SectionHeader
          label={t('globalSetting.correlatedRules.extraAggressive')}
          info={t('policy.riskAi.extraAggressive.info')}
          aggressivityLevel={CorrelatedRuleAggressivity.SuperAggressive}
          count={aggressivityCounts[CorrelatedRuleAggressivity.SuperAggressive]}
        />
        <AggressivityTable
          data={extraAggressiveActionData}
          onOpenEditModal={(actionData) =>
            handleActionEdit(
              actionData,
              t('globalSetting.correlatedRules.extraAggressive')
            )
          }
        />
      </Section>
      <Section>
        <SectionHeader
          label={t('globalSetting.correlatedRules.aggressive')}
          info={t('policy.riskAi.aggressive.info')}
          aggressivityLevel={CorrelatedRuleAggressivity.Aggressive}
          count={aggressivityCounts[CorrelatedRuleAggressivity.Aggressive]}
        />
        <AggressivityTable
          data={aggressiveActionData}
          onOpenEditModal={(actionData) =>
            handleActionEdit(
              actionData,
              t('globalSetting.correlatedRules.aggressive')
            )
          }
        />
      </Section>
      <Section>
        <SectionHeader
          label={t('globalSetting.correlatedRules.moderate')}
          info={t('policy.riskAi.moderate.info')}
          aggressivityLevel={CorrelatedRuleAggressivity.Moderate}
          count={aggressivityCounts[CorrelatedRuleAggressivity.Moderate]}
        />
        <AggressivityTable
          data={moderateActionData}
          onOpenEditModal={(actionData) =>
            handleActionEdit(
              actionData,
              t('globalSetting.correlatedRules.moderate')
            )
          }
        />
      </Section>
      {actionDataToEdit && (
        <EditableActionModal
          action={actionDataToEdit.action}
          subjectTag={actionDataToEdit.subjectTag}
          disclaimer={actionDataToEdit.disclaimer}
          name={`${actionDataToEdit.threatType} - ${actionDataToEdit.aggressivityLevel}`}
          isOpen={!!actionDataToEdit}
          onClose={() => setActionDataToEdit(null)}
          onConfirm={(newLabel) => {
            if (actionDataToEdit.action === ActionEnum.addDisclaimer) {
              actionDataToEdit.setDisclaimer(newLabel);
            } else if (actionDataToEdit.action === ActionEnum.tagSubject) {
              actionDataToEdit.setSubjectTag(newLabel);
            }
            setActionDataToEdit(null);
          }}
        />
      )}
    </Stack>
  );
};

const AggressivityTable = (props: {
  data: AnomalyActionData[];
  onOpenEditModal: (actionData: AnomalyActionData) => void;
}) => {
  const { data, onOpenEditModal } = props;
  const { t } = useTranslation();
  const [colorMode] = useColorMode();
  const [colorStyle] = useColorStyle({ colorMode });
  const contextStore = usePolicyContext();
  const contextSelector = storeSelector(contextStore);
  const { allRulesActionOptions } = contextSelector(
    raiActionDataSelector,
    isEqual
  );

  const columns = useMemo(() => {
    const colHelper = createColumnHelper<AnomalyActionData>();
    return [
      colHelper.accessor('threatType', {
        header: () => (
          <TextEllipsis>
            {t('riskAi.table.threat.header.analysisResult')}
          </TextEllipsis>
        ),
        cell: (cell) => <TextEllipsis>{cell.getValue()}</TextEllipsis>,
        minSize: 250,
      }),
      colHelper.display({
        id: 'action',
        header: () => <TextEllipsis>{t('common.label.action')}</TextEllipsis>,
        cell: (cell) => {
          const { action, setAction } = cell.row.original;
          return (
            <Flex>
              <EditableActionCell
                action={action}
                options={allRulesActionOptions}
                onActionChange={setAction}
                onModalOpen={() => onOpenEditModal(cell.row.original)}
                selectProps={{
                  width: 252,
                }}
              />
            </Flex>
          );
        },
        minSize: 308,
      }),
    ];
  }, [allRulesActionOptions, onOpenEditModal, t]);

  const table = useReactTable({
    columns,
    data,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <Table
      width="max-content"
      borderTop="1px solid"
      borderLeft="1px solid"
      borderRight="1px solid"
      borderColor={colorStyle.background.fourth}
    >
      <TableHeader>
        {table.getHeaderGroups().map((headerGroup) => (
          <TableRow key={headerGroup.id}>
            {headerGroup.headers.map((header, index) => (
              <TableCell
                key={header.id}
                minWidth={header.column.columnDef.minSize}
                borderRight={
                  index !== headerGroup.headers.length - 1
                    ? '1px solid'
                    : undefined
                }
                borderColor={colorStyle.background.fourth}
              >
                {flexRender(
                  header.column.columnDef.header,
                  header.getContext()
                )}
              </TableCell>
            ))}
          </TableRow>
        ))}
      </TableHeader>
      <TableBody width="max-content">
        {table.getRowModel().rows.map((row) => (
          <TableRow key={row.id}>
            {row.getVisibleCells().map((cell, index) => (
              <TableCell
                key={cell.id}
                display="flex"
                alignItems="center"
                minWidth={cell.column.columnDef.minSize}
                borderRight={
                  index !== row.getVisibleCells().length - 1
                    ? '1px solid'
                    : undefined
                }
                borderColor={colorStyle.background.fourth}
              >
                {flexRender(cell.column.columnDef.cell, cell.getContext())}
              </TableCell>
            ))}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};
